import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type {Dayjs} from 'dayjs';
import type {IAvailability, ILocation, IShift, TMaybeLocation} from '../store';

dayjs.extend(utc);
dayjs.extend(timezone);


class DateTime {
	//
	//
	// CONVERSION METHODS
	//

	/**
	 * converts a Dayjs object to seconds-after-midnight for its given date
	 *
	 * If not given a date, one is created for "today".
	 *
	 * For the opposite operation, see convertSecondsToDayjs().
	 *
	 * @param date
	 */
	convertDayjsToSeconds(date?: Dayjs): number {
		let workingDate: Dayjs;
		if(typeof date !== 'undefined') {
			workingDate = dayjs(date); // clones
		} else {
			workingDate = dayjs();
		}

		const startOfDate = workingDate.startOf('day');
		return workingDate.unix() - startOfDate.unix();
	}

	/**
	 * converts seconds-after-midnight to a Dayjs object
	 *
	 * If given a Dayjs object, the seconds are added to the start of its
	 * day. Otherwise, a Dayjs object is created for "today".
	 *
	 * For the opposite operation, see convertDayjsToSeconds().
	 *
	 * @param secondsAfterMidnight
	 * @param onDate
	 */
	convertSecondsToDayjs(secondsAfterMidnight: number, onDate?: Dayjs): Dayjs {
		let workingDate: Dayjs;
		if(typeof onDate !== 'undefined') {
			workingDate = dayjs(onDate); // clones
		} else {
			workingDate = dayjs();
		}

		return workingDate.startOf('day').add(secondsAfterMidnight, 'second');
	}

	convertUnixToLocationTimezone(unixTimestamp: number, location: ILocation): Dayjs {
		return dayjs.unix(unixTimestamp).tz(location.timezoneId);
	}


	//
	//
	// LOCATION METHODS
	//

	/**
	 * returns a Day.js object for "now" in the given location's timezone
	 *
	 * @param location
	 */
	locationGetLocalNow(location: TMaybeLocation): Dayjs {
		if(!location) {
			console.error('No location provided to locationGetLocalNow method.');
			throw new Error('No location provided.');
		}

		return dayjs().tz(location.timezoneId);
	}

	/**
	 * returns all shifts from given list occurring during or after current location's time on dayToCheck
	 *
	 * If no shifts remain for the given day, an empty array is returned. If
	 * the given day is "today", this method has no effect and just returns
	 * (the reference to) the input list.
	 */
	locationGetRemainingShiftsForDay(location: TMaybeLocation, dayToCheck: Dayjs, shifts: IShift[]): IShift[] {
		if(!location) {
			console.error('No location provided to locationGetRemainingShiftsForDay method.');
			throw new Error('No location provided.');
		}

		const locationNow = this.locationGetLocalNow(location);

		// If today
		if(dayToCheck.isSame(locationNow, 'day')) {
			// Get the current time in seconds after midnight
			const secondsAfterMidnight = this.convertDayjsToSeconds(locationNow);

			/*
			 * Get the index of the first shift that the current secondsAfterMidnight falls
			 * in or precedes. Shifts are sorted and merged when being sent back from the server.
			 */
			const index = shifts.findIndex((shift) => {
				return (
					this.shiftIncludesTimestamp(shift, secondsAfterMidnight) ||
					(shift.startSeconds > secondsAfterMidnight) // Check if this shift is upcoming
				);
			});

			if(index === -1) {
				return [];
			} else {
				return shifts.slice(index);
			}
		}

		return shifts;
	}


	//
	//
	// PICKUP TIME METHODS
	//

	/**
	 * checks that given pickup time is valid
	 *
	 * If given an optional waitTime, this method checks that the
	 * pickup time is at least waitTime minutes out.
	 *
	 * @param pickupDateTime
	 * @param waitTime
	 */
	pickupTimeValidate(pickupDateTime: Dayjs, waitTime = 0) {
		const now = dayjs();

		// check that this date is still valid
		if(pickupDateTime.isBefore(now)) {
			console.warn(`Selected pickup time of ${pickupDateTime.format('YYYY-MM-DD HH:mm')} has expired`, pickupDateTime);
			throw new Error('Selected pickup time has expired');
		}

		if(waitTime > 0) {
			// factor in wait time
			const pickupDiff = pickupDateTime.diff(now, 'minutes');

			if(pickupDiff < waitTime) {
				console.warn(`Selected pickup time of ${pickupDateTime.format('YYYY-MM-DD HH:mm')} is too soon`, pickupDateTime);
				throw new Error(`Location requires at least ${waitTime} minutes to fulfill an order`);
			}
		}
	}


	//
	//
	// SHIFT METHODS
	//

	/**
	 * indicates if given seconds-after-midnight falls in given shift
	 */
	shiftIncludesTimestamp(shift: IShift, secondsAfterMidnight: number): boolean {
		return shift.startSeconds <= secondsAfterMidnight && shift.endSeconds >= secondsAfterMidnight;
	}

	shiftNextOpen(shifts: IAvailability[]): Dayjs | null {
		const nextOpenShifts = shifts.find(avail => avail.shifts.length > 0);
		if(nextOpenShifts) {
			return this.convertSecondsToDayjs(nextOpenShifts.shifts[0].startSeconds, nextOpenShifts.date);
		}

		return null;
	}
}

export const DateTimeService = new DateTime();
